import { navigateBackOrFallback } from "../../shared/screen-shell/screen-navigation.utils";
import {Component, OnInit} from "@angular/core";
import {Location} from "@angular/common";
import {ActivatedRoute, Router} from "@angular/router";
import {BehaviorSubject} from "rxjs";
import { DelayedSpinnerService } from "../../shared/services/delayed-spinner.service";
import {Chat} from "../../shared/models/chat";
import {Player} from "../../shared/models/player";
import {Event} from "../../shared/models/event";
import {PlayerEventInfo} from "../../shared/models/playerEventInfo";
import {ChatService} from "../../shared/services/chat.service";
import {EventService} from "../../shared/services/event.service";
import {PlayerService} from "../../shared/services/player.service";

@Component({
  selector: "chat-settings",
  templateUrl: "./chat-settings.component.html",
  styleUrls: ["../chat.component.css"],
  standalone: false
})
export class ChatSettingsComponent implements OnInit {

  public chat: Chat;
  public currentPlayer: Player;
  public viewedByManager = false;
  public showLeaveDialog = false;
  public memberFilter = "";
  public botSettingSaving = false;
  public readonly settingsReady$ = new BehaviorSubject<boolean>(false);

  constructor(
    private chatService: ChatService,
    private eventService: EventService,
    private playerService: PlayerService,
    private route: ActivatedRoute,
    private router: Router,
    private location: Location,
    private spinner: DelayedSpinnerService
  ) {
  }

  ngOnInit(): void {
    this.spinner.show();
    this.settingsReady$.next(false);
    this.currentPlayer = this.playerService.getCurrentPlayer();
    const chatId = this.route.snapshot.params["chatId"];
    if (!chatId) {
      this.router.navigate(["/chat-list"]);
      return;
    }
    const prepared = this.route.snapshot.data['screen'];
    if (prepared?.chat) {
      this.chat = prepared.chat;
      this.viewedByManager = this.chat.contact?.id === this.currentPlayer?.id;
      this.spinner.hide();
      this.settingsReady$.next(true);
      return;
    }
    this.chatService.getChatById(chatId).subscribe(chat => {
      this.chat = chat;
      this.viewedByManager = !!(chat && chat.contact && this.currentPlayer && this.currentPlayer.id === chat.contact.id);
      this.spinner.hide();
      this.settingsReady$.next(true);
    }, error => {
      console.log(error);
      this.spinner.hide();
      this.router.navigate(["/chat-list"]);
    });
  }

  public goBack(): void {
    navigateBackOrFallback(this.location, this.router, ["/chat-list"]);
  }

  public openMatchScreen(): void {
    if (this.chat && this.chat.entityType === "EVENT" && this.chat.entityId) {
      this.router.navigate(["/event-players/false", { eventId: this.chat.entityId }]);
    }
  }

  public toggleFootballerBot(): void {
    if (!this.chat || !this.canConfigureFootballerBot() || this.botSettingSaving) {
      return;
    }
    const nextValue = this.chat.footballerBotEnabled === false;
    this.botSettingSaving = true;
    this.chatService.setFootballerBotEnabled(this.chat.id, nextValue).subscribe(chat => {
      this.chat = chat;
      this.botSettingSaving = false;
    }, error => {
      console.log(error);
      this.botSettingSaving = false;
    });
  }

  public modifyChat(): void {
    localStorage.setItem("chat", JSON.stringify(this.chat));
    this.router.navigate(["/chat-detail"]);
  }

  public leaveChat(): void {
    if (!this.canLeaveChat()) return;
    this.spinner.show();
    this.chatService.leaveChat(this.chat, this.currentPlayer).subscribe(() => {
      this.spinner.hide();
      this.router.navigate(["/chat-list"]);
    }, error => {
      console.log(error);
      this.spinner.hide();
    });
  }

  public openPlayerDetail(player: Player): void {
    if (!player || !player.id) {
      return;
    }
    if (this.chat && this.chat.entityType === "EVENT" && this.chat.entityId) {
      this.openMatchPlayerDetail(player);
      return;
    }
    this.openPlayerDetailWithEvent(player, null);
  }

  private openMatchPlayerDetail(player: Player): void {
    this.spinner.show();
    this.eventService.getEvent(this.chat.entityId).subscribe(event => {
      this.openPlayerDetailWithEvent(player, event);
      this.spinner.hide();
    }, error => {
      console.log(error);
      this.openPlayerDetailWithEvent(player, null);
      this.spinner.hide();
    });
  }

  private openPlayerDetailWithEvent(player: Player, event: Event): void {
    const playerEventInfo = this.findPlayerEventInfo(event, player) || this.minimalPlayerEventInfo(player);
    const eventForPlayerDetail = event || this.minimalEvent(playerEventInfo);

    localStorage.removeItem("chatPlayer");
    localStorage.removeItem("openPlayerDetailForPlayerId");
    localStorage.removeItem("openPlayerDetailForEventId");
    localStorage.setItem("chatSettingsSourceChatId", this.chat.id);
    localStorage.setItem("chatSettingsCanManage", String(this.canManageChatMembers()));
    localStorage.setItem("peiForPlayerDetail", JSON.stringify(playerEventInfo));
    localStorage.setItem("eventForPlayerDetail", JSON.stringify(eventForPlayerDetail));
    this.router.navigate(["/player-detail", true]);
  }

  private findPlayerEventInfo(event: Event, player: Player): PlayerEventInfo {
    if (!event || !event.playerEventInfos || !player || !player.id) {
      return null;
    }
    return event.playerEventInfos.find(playerEventInfo =>
      playerEventInfo && playerEventInfo.player && playerEventInfo.player.id === player.id
    ) || null;
  }

  private minimalPlayerEventInfo(player: Player): PlayerEventInfo {
    const playerEventInfo = new PlayerEventInfo();
    playerEventInfo.eventId = this.chat.entityId;
    playerEventInfo.player = player;
    return playerEventInfo;
  }

  private minimalEvent(playerEventInfo: PlayerEventInfo): Event {
    const event = new Event();
    event.id = this.chat.entityId;
    event.name = this.chat.displayName || this.chat.name;
    event.viewedByManager = this.viewedByManager;
    event.playerEventInfos = [playerEventInfo];
    return event;
  }

  public canManageChat(): boolean {
    return !!(this.chat && this.chat.entityType !== "EVENT"
      && ((this.viewedByManager && this.chat.priority >= 10)
        || (this.currentPlayer && this.currentPlayer.superAdmin && this.chat.priority < 10)));
  }

  public canLeaveChat(): boolean {
    return !!(this.chat && this.chat.entityType !== "EVENT" && !this.viewedByManager && this.chat.priority >= 10);
  }

  public canConfigureFootballerBot(): boolean {
    return !!(this.chat && this.chat.entityType === "EVENT" && this.chat.id && this.viewedByManager);
  }

  public canManageChatMembers(): boolean {
    return !!(this.chat && this.chat.entityType !== "EVENT" && this.chat.id && (this.viewedByManager || this.canManageChat()));
  }

  public openAddMembers(): void {
    if (!this.canManageChatMembers()) {
      return;
    }
    this.router.navigate(["/chat-add-members", {chatId: this.chat.id}]);
  }

  public filteredMembers(): Player[] {
    const filter = (this.memberFilter || "").trim().toLowerCase();
    return (this.chat && this.chat.players ? this.chat.players : []).filter(player => {
      if (!filter) {
        return true;
      }
      return [
        player.nickName,
        player.name,
        player.email
      ].some(value => value && value.toLowerCase().indexOf(filter) >= 0);
    });
  }

  public getPlayerDisplayName(player: Player): string {
    return player && (player.nickName || player.name || player.email) ? (player.nickName || player.name || player.email) : "";
  }

  public getPlayerInitials(player: Player): string {
    const value = this.getPlayerDisplayName(player);
    const parts = value.trim().split(/\s+/).filter(part => !!part);
    if (parts.length === 0) {
      return "?";
    }
    if (parts.length === 1) {
      return parts[0].substring(0, 2).toUpperCase();
    }
    return (parts[0].charAt(0) + parts[1].charAt(0)).toUpperCase();
  }
}
